/**
 * Wrapper button for the design library button to pass our router specific link
 */

import { type ExoticComponent, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { type ButtonProps, type ButtonVariant, Button as DesignLibraryButton } from '@mweb/zappos-ui/Button';

import { cn } from 'helpers/classnames';

const rebrandButtonClasses: Record<ButtonVariant, string> = {
  filled: cn(
    'zappos:bg-gray-900',
    'zappos:border-gray-900 zappos:border zappos:border-solid zappos:rounded-sm',
    `zappos:font-sans zappos:font-medium zappos:leading-[1.2] zappos:text-sm zappos:text-gray-0 zappos:tracking-[0.05em] zappos:uppercase`,
    'zappos:active:bg-gray-500',
    'zappos:disabled:bg-gray-200 zappos:disabled:border-[transparent] zappos:disabled:text-gray-400',
    'zappos:hover:bg-gray-700 hover:text-[#707070]' // 6pm support
  ),
  outlined: cn(
    'zappos:bg-gray-0',
    'zappos:border-gray-900 zappos:border zappos:border-solid zappos:rounded-sm',
    `zappos:font-sans zappos:font-medium zappos:leading-[1.2] zappos:text-sm zappos:text-gray-900 zappos:tracking-[0.05em] zappos:uppercase`,
    'zappos:active:bg-gray-100',
    'zappos:disabled:bg-gray-50 zappos:disabled:text-gray-400',
    'zappos:hover:bg-gray-50 hover:text-[#707070]' // 6pm support
  ),
  outlinedReversed: cn(
    'zappos:rounded-sm',
    `zappos:font-sans zappos:font-medium zappos:leading-[1.2] zappos:text-sm zappos:tracking-[0.05em] zappos:uppercase`,
    'zappos:hover:border-gray-0 zappos:hover:text-gray-0 hover:border-[#707070] hover:text-[#707070]'
  ),
  text: cn(`zappos:font-sans`, 'zappos:hover:text-gray-900 hover:text-[#707070]'), // 6pm support
  textReversed: cn(`zappos:font-sans`, 'zappos:hover:text-gray-0 hover:text-[#707070]') // 6pm support
} as const;

type ButtonWrapperProps = Omit<ButtonProps, 'linkComponent' | 'linkProps'>;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonWrapperProps>(({ className, href, variant, ...rest }, ref) => (
  <DesignLibraryButton
    className={cn(rebrandButtonClasses[variant ?? 'filled'], className)}
    {...rest}
    href={href}
    linkComponent={Link as ExoticComponent}
    linkProps={{ to: href }}
    ref={ref}
    variant={variant}
  />
));

Button.displayName = 'Button';

export default Button;
